// The farm map: terrain grid, props and planted crops, plus the autotile rules
// that pick which sprite a tile renders as.
//
// There is more than one map now — the farm and the mine's floors — so the grid
// itself lives in a `MapLayer` and `World` is the thing that owns them, keeps one
// active, and answers questions about it. Everything that used `world.tiles`
// before still works: those are getters onto the active layer.
import {
  MAP_H,
  MAP_W,
  REGIONS,
  SOIL_START,
  type SeasonId,
} from "./config.ts";
import { SEASON_VARIANT } from "../render/extras.ts";

export enum Tile {
  Grass = 0,
  Dirt = 1,
  Stone = 2,
  Wood = 3,
  Water = 4,
  SoilDry = 5,
  SoilWet = 6,
  Gravel = 7,
  Plank = 8,
  Bridge = 9,
  CaveFloor = 10,
  CaveWall = 11,
}

export enum Prop {
  None = 0,
  FenceH = 1,
  FenceV = 2,
  FenceCorner = 3,
  FencePost = 4,
  FencePostH = 5,
  TreeA = 6,
  TreeB = 7,
  Stump = 8,
  Bin = 9,
  House = 10,
  BushBerry = 11,
  BushPicked = 12,
  Mushroom = 13,
  Flower = 14,
  Stick = 15,
  StoneSmall = 16,
  Weed = 17,
  // 20..29 are the build menu; the numbers are mirrored in config.BUILDABLES
  Gate = 20,
  Chest = 21,
  Scarecrow = 22,
  Sprinkler = 23,
  Coop = 24,
  Trough = 25,
  Barn = 26,
  Hay = 27,
  Mailbox = 28,
  Well = 29,
  Shop = 30,
  CaveEntrance = 31,
  Greenhouse = 32,
  Sign = 33,
  SignForSale = 34,
  Rock = 35,
  RockLarge = 36,
  OreCopper = 37,
  OreIron = 38,
  OreGold = 39,
  LadderDown = 40,
  LadderUp = 41,
  /** invisible solid: the rest of a multi-tile building's footprint */
  Blocked = 63,
}

export interface Crop {
  id: string;
  /** 0 seed, 1 sprout, 2 young, 3 ripe, 4 withered */
  stage: number;
  /** watered days banked toward the next stage */
  progress: number;
  watered: boolean;
  /** consecutive days left dry */
  dry: number;
  /** 0 normal, 1 silver, 2 gold — rolled when it ripens */
  quality: number;
}

/** A crop record with the fields older saves are missing filled in. */
export function makeCrop(o: Partial<Crop> & { id: string }): Crop {
  return {
    id: o.id,
    stage: o.stage ?? 0,
    progress: o.progress ?? 0,
    watered: o.watered ?? false,
    dry: o.dry ?? 0,
    quality: o.quality ?? 0,
  };
}

const PROP_SPRITE: Partial<Record<Prop, string>> = {
  [Prop.None]: "",
  [Prop.FenceH]: "fence_h",
  [Prop.FenceV]: "fence_v",
  [Prop.FenceCorner]: "fence_corner",
  [Prop.FencePost]: "fence_post",
  [Prop.FencePostH]: "fence_post_h",
  [Prop.TreeA]: "tree_a",
  [Prop.TreeB]: "tree_b",
  [Prop.Stump]: "stump",
  [Prop.Bin]: "crate_full",
  [Prop.House]: "house",
  [Prop.BushBerry]: "bush_berry",
  [Prop.BushPicked]: "bush_berry_picked",
  [Prop.Mushroom]: "item_mushroom",
  [Prop.Flower]: "flower_patch_01",
  [Prop.Stick]: "item_stick",
  [Prop.StoneSmall]: "item_stone",
  [Prop.Weed]: "weed_01",
  [Prop.Gate]: "gate_closed",
  [Prop.Chest]: "chest",
  [Prop.Scarecrow]: "scarecrow_0",
  [Prop.Sprinkler]: "sprinkler",
  [Prop.Coop]: "coop",
  [Prop.Trough]: "feed_trough",
  [Prop.Barn]: "barn",
  [Prop.Hay]: "hay_bale",
  [Prop.Mailbox]: "mailbox",
  [Prop.Well]: "well",
  [Prop.Shop]: "shop",
  [Prop.CaveEntrance]: "cave_entrance",
  [Prop.Greenhouse]: "greenhouse",
  [Prop.Sign]: "sign",
  [Prop.SignForSale]: "sign_forsale",
  [Prop.Rock]: "rock_small",
  [Prop.RockLarge]: "rock_large",
  [Prop.OreCopper]: "ore_copper",
  [Prop.OreIron]: "ore_iron",
  [Prop.OreGold]: "ore_gold",
  [Prop.LadderDown]: "cave_entrance",
  [Prop.LadderUp]: "cave_entrance",
  [Prop.Blocked]: "",
};

/** Props drawn as tall objects standing on their tile rather than flat on it. */
const PROP_TALL = new Set<Prop>([
  Prop.TreeA,
  Prop.TreeB,
  Prop.Stump,
  Prop.Bin,
  Prop.House,
  Prop.BushBerry,
  Prop.BushPicked,
  Prop.Chest,
  Prop.Scarecrow,
  Prop.Sprinkler,
  Prop.Coop,
  Prop.Barn,
  Prop.Shop,
  Prop.Greenhouse,
  Prop.Well,
  Prop.Mailbox,
  Prop.Sign,
  Prop.SignForSale,
  Prop.CaveEntrance,
]);

/** Props you can walk straight over. */
const PROP_WALKABLE = new Set<Prop>([
  Prop.None,
  Prop.Stump,
  Prop.Mushroom,
  Prop.Flower,
  Prop.Stick,
  Prop.StoneSmall,
  Prop.Weed,
  Prop.Gate,
  Prop.LadderDown,
  Prop.LadderUp,
]);

/** Forage you clear by walking up and pressing the harvest key. */
export const FORAGE_PROPS: Partial<Record<Prop, string>> = {
  [Prop.BushBerry]: "forage:berry",
  [Prop.Mushroom]: "forage:mushroom",
  [Prop.Flower]: "forage:flower",
  [Prop.Stick]: "forage:stick",
  [Prop.StoneSmall]: "forage:stone",
};

/** Buildings wider or taller than a tile, in whole tiles, anchored bottom-centre. */
const PROP_FOOTPRINT: Partial<Record<Prop, { w: number; h: number }>> = {
  [Prop.House]: { w: 3, h: 3 },
  [Prop.Coop]: { w: 3, h: 2 },
  [Prop.Barn]: { w: 4, h: 3 },
  [Prop.Shop]: { w: 3, h: 3 },
  [Prop.Greenhouse]: { w: 4, h: 3 },
  [Prop.CaveEntrance]: { w: 2, h: 2 },
};

export function footprintOf(p: Prop): { w: number; h: number } {
  return PROP_FOOTPRINT[p] ?? { w: 1, h: 1 };
}

export function hash2(x: number, y: number, s = 0): number {
  let n = (x * 374761393 + y * 668265263 + s * 982451653) | 0;
  n = (n ^ (n >> 13)) * 1274126177;
  n = n ^ (n >> 16);
  return ((n >>> 0) % 4096) / 4096;
}

export interface HomeSpots {
  bed: { x: number; y: number };
  door: { x: number; y: number };
  bin: { x: number; y: number };
  spawn: { x: number; y: number };
  shop: { x: number; y: number };
  cave: { x: number; y: number };
}

export interface MapLayer {
  id: string;
  tiles: Uint8Array;
  props: Uint8Array;
  crops: Map<number, Crop>;
  /** soil fertility 0..100, only meaningful under tilled tiles */
  fert: Uint8Array;
  /** index+1 of the last crop grown here, so rotation can be rewarded */
  last: Uint8Array;
}

function blankLayer(id: string): MapLayer {
  return {
    id,
    tiles: new Uint8Array(MAP_W * MAP_H),
    props: new Uint8Array(MAP_W * MAP_H),
    crops: new Map(),
    fert: new Uint8Array(MAP_W * MAP_H),
    last: new Uint8Array(MAP_W * MAP_H),
  };
}

export class World {
  readonly w = MAP_W;
  readonly h = MAP_H;
  readonly layers = new Map<string, MapLayer>();
  active: MapLayer;

  /** Which season the map paints itself in. */
  season: SeasonId = "spring";
  /** Mine floor currently generated into the "mine" layer, 0 = not in the mine. */
  mineFloor = 0;
  /** Regions the player has bought. */
  readonly owned = new Set<string>();

  readonly home: HomeSpots = {
    bed: { x: 7, y: 5 },
    door: { x: 7, y: 7 },
    bin: { x: 12, y: 8 },
    spawn: { x: 7.5, y: 8.5 },
    shop: { x: 30, y: 16 },
    cave: { x: 48, y: 22 },
  };

  constructor() {
    this.layers.set("farm", blankLayer("farm"));
    this.layers.set("mine", blankLayer("mine"));
    this.active = this.layers.get("farm")!;
    this.generate();
  }

  // ------------------------------------------------------------- layer view

  get tiles(): Uint8Array {
    return this.active.tiles;
  }

  get props(): Uint8Array {
    return this.active.props;
  }

  get crops(): Map<number, Crop> {
    return this.active.crops;
  }

  get fert(): Uint8Array {
    return this.active.fert;
  }

  get onFarm(): boolean {
    return this.active.id === "farm";
  }

  layer(id: string): MapLayer {
    return this.layers.get(id)!;
  }

  enter(id: string): void {
    this.active = this.layers.get(id)!;
  }

  // ----------------------------------------------------------------- basics

  idx(x: number, y: number): number {
    return y * this.w + x;
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.w && y < this.h;
  }

  tile(x: number, y: number): Tile {
    if (!this.inBounds(x, y)) return this.onFarm ? Tile.Water : Tile.CaveWall;
    return this.tiles[this.idx(x, y)] as Tile;
  }

  setTile(x: number, y: number, t: Tile): void {
    if (this.inBounds(x, y)) this.tiles[this.idx(x, y)] = t;
  }

  prop(x: number, y: number): Prop {
    if (!this.inBounds(x, y)) return Prop.None;
    return this.props[this.idx(x, y)] as Prop;
  }

  setProp(x: number, y: number, p: Prop): void {
    if (this.inBounds(x, y)) this.props[this.idx(x, y)] = p;
  }

  cropAt(x: number, y: number): Crop | undefined {
    return this.crops.get(this.idx(x, y));
  }

  fertility(x: number, y: number): number {
    return this.inBounds(x, y) ? this.fert[this.idx(x, y)]! : 0;
  }

  setFertility(x: number, y: number, v: number): void {
    if (this.inBounds(x, y)) this.fert[this.idx(x, y)] = Math.max(0, Math.min(100, Math.round(v)));
  }

  /** Solid for the player. */
  blocked(x: number, y: number): boolean {
    if (!this.inBounds(x, y)) return true;
    const t = this.tile(x, y);
    if (t === Tile.Water || t === Tile.CaveWall) return true;
    if (!this.owns(x, y)) return true;
    return !PROP_WALKABLE.has(this.prop(x, y));
  }

  /** False while the tile belongs to a region the player has not bought. */
  owns(x: number, y: number): boolean {
    if (!this.onFarm) return true;
    for (const r of REGIONS) {
      if (x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1) return this.owned.has(r.id);
    }
    return true;
  }

  /** Inside a greenhouse the season never stops anything growing. */
  isGreenhouse(x: number, y: number): boolean {
    if (!this.onFarm) return false;
    for (const r of REGIONS) {
      if (r.greenhouse && this.owned.has(r.id) && x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1) return true;
    }
    return false;
  }

  isFarmable(x: number, y: number): boolean {
    return this.tile(x, y) === Tile.Grass && this.prop(x, y) === Prop.None && this.owns(x, y);
  }

  isSoil(x: number, y: number): boolean {
    const t = this.tile(x, y);
    return t === Tile.SoilDry || t === Tile.SoilWet;
  }

  isOpenWater(x: number, y: number): boolean {
    return this.tile(x, y) === Tile.Water;
  }

  nearWater(x: number, y: number): boolean {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (this.tile(x + dx, y + dy) === Tile.Water) return true;
      }
    }
    if (this.prop(x, y) === Prop.Well) return true;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (this.prop(x + dx, y + dy) === Prop.Well) return true;
      }
    }
    return false;
  }

  /** Find a prop within `r` tiles, used by scarecrows, coops and troughs. */
  propWithin(x: number, y: number, r: number, p: Prop): boolean {
    const lo = Math.max(0, Math.floor(y - r));
    const hi = Math.min(this.h - 1, Math.ceil(y + r));
    for (let yy = lo; yy <= hi; yy++) {
      for (let xx = Math.max(0, Math.floor(x - r)); xx <= Math.min(this.w - 1, Math.ceil(x + r)); xx++) {
        if (this.prop(xx, yy) === p) return true;
      }
    }
    return false;
  }

  /** The house's anchor tile — the doormat is the tile below it. */
  findHouseDoor(): { x: number; y: number } | null {
    return this.onFarm ? this.findProp(Prop.House) : null;
  }

  findProp(p: Prop): { x: number; y: number } | null {
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) if (this.prop(x, y) === p) return { x, y };
    }
    return null;
  }

  // ---------------------------------------------------------------- sprites

  propSprite(p: Prop): string {
    const id = PROP_SPRITE[p] ?? "";
    return this.seasonal(id);
  }

  propIsTall(p: Prop): boolean {
    return PROP_TALL.has(p);
  }

  /** The crop sprite, with withered crops in winter reading as frost-killed. */
  cropSpriteAt(crop: Crop): string {
    return `${crop.id}_${crop.stage}_${["seed", "sprout", "young", "ripe", "withered"][crop.stage]}`;
  }

  /** Appends the season suffix when the sprite has a seasonal variant. */
  private seasonal(id: string): string {
    if (!id || this.season === "spring" || !this.onFarm) return id;
    return SEASON_VARIANT.get(`${id}|${this.season}`) ?? id;
  }

  /** Which atlas sprite this ground tile renders as, including autotile edges. */
  groundSprite(x: number, y: number): string {
    const t = this.tile(x, y);
    switch (t) {
      case Tile.Dirt:
        return "dirt_path";
      case Tile.Stone:
        return "stone_path";
      case Tile.Wood:
        return "wood_floor";
      case Tile.Gravel:
        return "path_gravel";
      case Tile.Plank:
        return "path_plank";
      case Tile.Bridge:
        return "bridge_h";
      case Tile.CaveFloor:
        return `cave_floor_0${1 + Math.floor(hash2(x, y, 12) * 3)}`;
      case Tile.CaveWall:
        return "cave_wall";
      case Tile.SoilDry:
        return this.fertility(x, y) >= 60 ? "soil_fertilised_dry" : "soil_dry";
      case Tile.SoilWet:
        return this.fertility(x, y) >= 60 ? "soil_fertilised_wet" : "soil_wet";
      case Tile.Water: {
        const suffix = edgeSuffix(this.mask(x, y, (t2) => t2 !== Tile.Water));
        return this.seasonal(suffix ? `water_edge_${suffix}` : "water");
      }
      default: {
        const isDirt = (t2: Tile) => t2 === Tile.Dirt;
        const m = this.mask(x, y, isDirt);
        if (m === 0) {
          const inner = this.innerSuffix(x, y, isDirt);
          if (inner) return this.seasonal(`grass_to_dirt_in_${inner}`);
          const v = hash2(x, y, 7);
          return this.seasonal(v > 0.72 ? "grass_02" : v > 0.4 ? "grass_03" : "grass_01");
        }
        if (popcount(m) >= 3) return "dirt_path";
        const suffix = edgeSuffix(m);
        return this.seasonal(suffix ? `grass_to_dirt_${suffix}` : "grass_01");
      }
    }
  }

  private mask(x: number, y: number, pred: (t: Tile) => boolean): number {
    let m = 0;
    if (pred(this.tile(x, y - 1))) m |= 1;
    if (pred(this.tile(x + 1, y))) m |= 2;
    if (pred(this.tile(x, y + 1))) m |= 4;
    if (pred(this.tile(x - 1, y))) m |= 8;
    return m;
  }

  private innerSuffix(x: number, y: number, pred: (t: Tile) => boolean): string | null {
    if (pred(this.tile(x + 1, y - 1))) return "ne";
    if (pred(this.tile(x - 1, y - 1))) return "nw";
    if (pred(this.tile(x + 1, y + 1))) return "se";
    if (pred(this.tile(x - 1, y + 1))) return "sw";
    return null;
  }

  // ------------------------------------------------------------- placement

  /** Stamp a building and fill the rest of its footprint with invisible solids. */
  place(x: number, y: number, p: Prop): void {
    const { w, h } = footprintOf(p);
    const x0 = x - Math.floor((w - 1) / 2);
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const tx = x0 + dx;
        const ty = y - dy;
        if (!this.inBounds(tx, ty)) continue;
        this.setProp(tx, ty, tx === x && ty === y ? p : Prop.Blocked);
      }
    }
  }

  /** True when a building of this size would fit here without overwriting. */
  fits(x: number, y: number, p: Prop): boolean {
    const { w, h } = footprintOf(p);
    const x0 = x - Math.floor((w - 1) / 2);
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const tx = x0 + dx;
        const ty = y - dy;
        if (!this.inBounds(tx, ty)) return false;
        if (!this.owns(tx, ty)) return false;
        if (this.prop(tx, ty) !== Prop.None) return false;
        if (this.cropAt(tx, ty)) return false;
        const t = this.tile(tx, ty);
        if (t === Tile.Water || t === Tile.CaveWall) return false;
      }
    }
    return true;
  }

  /** Remove a building and everything its footprint claimed. */
  clearFootprint(x: number, y: number): void {
    const p = this.prop(x, y);
    const { w, h } = footprintOf(p);
    const x0 = x - Math.floor((w - 1) / 2);
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) this.setProp(x0 + dx, y - dy, Prop.None);
    }
  }

  // ----------------------------------------------------------- generation

  private generate(): void {
    this.enter("farm");
    this.tiles.fill(Tile.Grass);
    this.fert.fill(0);

    // pond in the north-east
    const pond = { cx: 45, cy: 7, rx: 7.5, ry: 5 };
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const dx = (x - pond.cx) / pond.rx;
        const dy = (y - pond.cy) / pond.ry;
        const wobble = hash2(x, y, 3) * 0.18;
        if (dx * dx + dy * dy + wobble < 1) this.setTile(x, y, Tile.Water);
      }
    }

    // porch: the wooden deck the house stands on
    for (let y = 3; y <= 8; y++) {
      for (let x = 4; x <= 10; x++) this.setTile(x, y, Tile.Wood);
    }

    // stone steps down from the porch, then the dirt road running off the map
    for (let y = 9; y <= 12; y++) this.setTile(7, y, Tile.Stone);
    for (let x = 4; x < this.w; x++) this.setTile(x, 13, Tile.Dirt);
    for (let y = 14; y <= 19; y++) this.setTile(22, y, Tile.Dirt);
    for (let x = 26; x <= 34; x++) this.setTile(x, 17, Tile.Dirt);
    for (let y = 14; y <= 17; y++) this.setTile(30, y, Tile.Dirt);
    for (let y = 14; y <= 22; y++) this.setTile(48, y, Tile.Dirt);

    // a paved landing on the south bank, for filling the watering can
    for (let x = 42; x <= 45; x++) {
      if (this.tile(x, 12) !== Tile.Water) this.setTile(x, 12, Tile.Stone);
    }

    this.fenceRect(12, 20, 34, 38, 22);
    // the east plot is fenced too, but you do not own it yet
    this.fenceRect(36, 20, 53, 38, 44);

    // shipping crate beside the porch, house on the deck
    this.setProp(this.home.bin.x, this.home.bin.y, Prop.Bin);
    this.place(this.home.door.x, this.home.door.y, Prop.House);
    this.setProp(5, 8, Prop.Mailbox);

    // the village: one shop on the north road
    this.place(this.home.shop.x, this.home.shop.y, Prop.Shop);
    this.setProp(28, 17, Prop.Sign);

    // the mine mouth on the eastern track
    this.place(this.home.cave.x, this.home.cave.y, Prop.CaveEntrance);

    // trees: a ring around the map plus scattered woodland, never on paths,
    // inside a field, or where they would box the player in
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        if (this.tile(x, y) !== Tile.Grass) continue;
        if (this.prop(x, y) !== Prop.None) continue;
        if (x >= 11 && x <= 54 && y >= 19 && y <= 39) continue;
        if (x >= 3 && x <= 12 && y >= 2 && y <= 10) continue; // around the porch
        if (x >= 27 && x <= 33 && y >= 13 && y <= 18) continue; // around the shop
        if (x >= 45 && x <= 51 && y >= 19 && y <= 24) continue; // around the cave
        if (Math.abs(x - this.home.bin.x) <= 2 && Math.abs(y - this.home.bin.y) <= 2) continue;
        if (this.nearTile(x, y, Tile.Dirt) || this.nearTile(x, y, Tile.Stone)) continue;
        if (this.nearTile(x, y, Tile.Water)) continue;
        const border = x < 2 || y < 2 || x > this.w - 3 || y > this.h - 3;
        const n = hash2(x, y, 21);
        if (border || n > 0.9) {
          this.setProp(x, y, n > 0.5 ? Prop.TreeA : Prop.TreeB);
        }
      }
    }

    // for-sale signs on the plots you have not bought
    for (const r of REGIONS) {
      const sx = Math.round((r.x0 + r.x1) / 2);
      const sy = r.y1 + 1;
      if (this.inBounds(sx, sy) && this.prop(sx, sy) === Prop.None) this.setProp(sx, sy, Prop.SignForSale);
    }

    // keep the crate reachable whatever the tree pass decided
    this.setProp(this.home.bin.x, this.home.bin.y, Prop.Bin);

    this.scatterForage(30);
  }

  private fenceRect(x0: number, y0: number, x1: number, y1: number, gateX: number): void {
    for (let x = x0; x <= x1; x++) {
      if (x === gateX) {
        this.setProp(x, y0, Prop.Gate);
        continue;
      }
      this.setProp(x, y0, x % 4 === 0 ? Prop.FencePostH : Prop.FenceH);
      this.setProp(x, y1, x % 4 === 0 ? Prop.FencePostH : Prop.FenceH);
    }
    for (let y = y0 + 1; y < y1; y++) {
      this.setProp(x0, y, y % 4 === 0 ? Prop.FencePost : Prop.FenceV);
      this.setProp(x1, y, y % 4 === 0 ? Prop.FencePost : Prop.FenceV);
    }
    this.setProp(x0, y0, Prop.FenceCorner);
    this.setProp(x1, y0, Prop.FencePostH);
    this.setProp(x0, y1, Prop.FencePost);
    this.setProp(x1, y1, Prop.FencePost);
  }

  /** Sprinkle berries, mushrooms, flowers and litter through the woods. */
  scatterForage(n: number, seed = 1): number {
    let placed = 0;
    for (let i = 0; i < n * 12 && placed < n; i++) {
      const x = Math.floor(hash2(i, seed, 31) * this.w);
      const y = Math.floor(hash2(i, seed, 47) * this.h);
      if (!this.inBounds(x, y)) continue;
      if (this.tile(x, y) !== Tile.Grass) continue;
      if (this.prop(x, y) !== Prop.None) continue;
      const shade = this.nearProp(x, y, Prop.TreeA) || this.nearProp(x, y, Prop.TreeB);
      const r = hash2(i, seed, 53);
      let p: Prop;
      if (shade && r < 0.34) p = Prop.Mushroom;
      else if (r < 0.55) p = Prop.BushBerry;
      else if (r < 0.78) p = Prop.Flower;
      else if (r < 0.9) p = Prop.Stick;
      else p = Prop.StoneSmall;
      this.setProp(x, y, p);
      placed++;
    }
    return placed;
  }

  countProp(p: Prop): number {
    let n = 0;
    for (let i = 0; i < this.props.length; i++) if (this.props[i] === p) n++;
    return n;
  }

  private nearProp(x: number, y: number, p: Prop): boolean {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (this.prop(x + dx, y + dy) === p) return true;
      }
    }
    return false;
  }

  private nearTile(x: number, y: number, t: Tile): boolean {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (this.tile(x + dx, y + dy) === t) return true;
      }
    }
    return false;
  }

  /** Clear a bought region: fell the trees and take the for-sale sign down. */
  claimRegion(id: string): void {
    const r = REGIONS.find((v) => v.id === id);
    if (!r) return;
    this.owned.add(id);
    for (let y = r.y0; y <= r.y1; y++) {
      for (let x = r.x0; x <= r.x1; x++) {
        const p = this.prop(x, y);
        if (p === Prop.TreeA || p === Prop.TreeB || p === Prop.Stump) this.setProp(x, y, Prop.None);
      }
    }
    const sx = Math.round((r.x0 + r.x1) / 2);
    const sy = r.y1 + 1;
    if (this.prop(sx, sy) === Prop.SignForSale) this.setProp(sx, sy, Prop.None);
    if (r.greenhouse) {
      for (let y = r.y0 + 1; y < r.y1; y++) {
        for (let x = r.x0 + 1; x < r.x1; x++) {
          if (this.tile(x, y) === Tile.Grass) this.setTile(x, y, Tile.SoilDry);
          this.setFertility(x, y, SOIL_START + 20);
        }
      }
      this.place(Math.round((r.x0 + r.x1) / 2), r.y0 + 1, Prop.Greenhouse);
    }
  }

  // -------------------------------------------------------------- the mine

  /**
   * Cave floors are carved rather than drawn: start solid, wander a few tunnels
   * out from the ladder, then widen them. Every floor is a pure function of its
   * number, so walking back up and down again finds the same rooms.
   */
  generateMine(floor: number): { x: number; y: number } {
    const layer = this.layer("mine");
    const prev = this.active;
    this.active = layer;
    this.mineFloor = floor;
    layer.crops.clear();
    layer.tiles.fill(Tile.CaveWall);
    layer.props.fill(Prop.None);
    layer.fert.fill(0);

    const start = { x: Math.floor(this.w / 2), y: this.h - 5 };
    let x = start.x;
    let y = start.y;
    const carve = (cx: number, cy: number, r: number) => {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (dx * dx + dy * dy > r * r + 1) continue;
          if (cx + dx < 1 || cy + dy < 1 || cx + dx > this.w - 2 || cy + dy > this.h - 2) continue;
          this.setTile(cx + dx, cy + dy, Tile.CaveFloor);
        }
      }
    };
    carve(x, y, 3);
    for (let step = 0; step < 900; step++) {
      const r = hash2(step, floor, 61);
      if (r < 0.34) x += 1;
      else if (r < 0.62) x -= 1;
      else if (r < 0.88) y -= 1;
      else y += 1;
      x = Math.max(3, Math.min(this.w - 4, x));
      y = Math.max(3, Math.min(this.h - 4, y));
      carve(x, y, hash2(step, floor, 67) > 0.86 ? 3 : 2);
    }

    // rocks and ore on the floor tiles, richer the deeper you go
    for (let ty = 1; ty < this.h - 1; ty++) {
      for (let tx = 1; tx < this.w - 1; tx++) {
        if (this.tile(tx, ty) !== Tile.CaveFloor) continue;
        if (Math.abs(tx - start.x) < 4 && Math.abs(ty - start.y) < 4) continue;
        const r = hash2(tx, ty, 70 + floor);
        if (r > 0.955) this.setProp(tx, ty, Prop.OreGold);
        else if (r > 0.9 - floor * 0.012) this.setProp(tx, ty, Prop.OreIron);
        else if (r > 0.79) this.setProp(tx, ty, Prop.OreCopper);
        else if (r > 0.68) this.setProp(tx, ty, hash2(tx, ty, 71) > 0.6 ? Prop.RockLarge : Prop.Rock);
      }
    }

    this.setProp(start.x, start.y, Prop.LadderUp);
    // the way down: the floor tile furthest from the ladder
    let best = { x: start.x, y: start.y, d: -1 };
    for (let ty = 1; ty < this.h - 1; ty++) {
      for (let tx = 1; tx < this.w - 1; tx++) {
        if (this.tile(tx, ty) !== Tile.CaveFloor) continue;
        const d = (tx - start.x) ** 2 + (ty - start.y) ** 2;
        if (d > best.d) best = { x: tx, y: ty, d };
      }
    }
    this.setProp(best.x, best.y, Prop.LadderDown);

    this.active = prev;
    return start;
  }
}

function popcount(m: number): number {
  let c = 0;
  while (m) {
    c += m & 1;
    m >>= 1;
  }
  return c;
}

function edgeSuffix(m: number): string | null {
  switch (m) {
    case 1:
      return "n";
    case 2:
      return "e";
    case 4:
      return "s";
    case 8:
      return "w";
    case 3:
      return "ne";
    case 9:
      return "nw";
    case 6:
      return "se";
    case 12:
      return "sw";
    case 0:
      return null;
    default:
      // opposite pairs and denser cases: fall back to the first edge found
      if (m & 1) return "n";
      if (m & 2) return "e";
      if (m & 4) return "s";
      return "w";
  }
}
